import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import type { ImageGroup } from '../hooks/useDeclarations';
import { useDeclarations } from '../hooks/useDeclarations';
import { useUpsertDeclaration } from '../hooks/useUpsertDeclaration';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  max-width: 640px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Button = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }
`;

export const DeclarationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const { data: declarations } = useDeclarations();
  const existing = declarations?.find((d) => d.id === id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [featured, setFeatured] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [imageGroups, setImageGroups] = useState<ImageGroup[]>([]);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setDescription(existing.description ?? '');
      setDate(existing.date ?? '');
      setFeatured(Boolean(existing.featured));
      setImageUrl(existing.image_url ?? '');
      setAdditionalImages(existing.additional_images ?? []);
      setImageGroups((existing.image_groups ?? []).map((g) => ({ title: g.title ?? '', images: g.images ?? [] })));
    }
  }, [existing]);

  const upsert = useUpsertDeclaration();

  const uploadToStorage = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleUpload = async (file: File) => {
    const publicUrl = await uploadToStorage(file);
    setImageUrl(publicUrl);
  };

  const handleAdditionalUpload = async (file: File) => {
    const publicUrl = await uploadToStorage(file);
    setAdditionalImages((prev) => [...prev, publicUrl]);
  };

  const handleGroupImageUpload = async (groupIndex: number, file: File) => {
    const publicUrl = await uploadToStorage(file);
    setImageGroups((prev) =>
      prev.map((group, idx) => (idx === groupIndex ? { ...group, images: [...(group.images ?? []), publicUrl] } : group)),
    );
  };

  const removeGroupImage = (groupIndex: number, imageIndex: number) => {
    setImageGroups((prev) =>
      prev.map((group, idx) =>
        idx === groupIndex ? { ...group, images: (group.images ?? []).filter((_, i) => i !== imageIndex) } : group,
      ),
    );
  };

  const removeGroup = (groupIndex: number) => {
    setImageGroups((prev) => prev.filter((_, idx) => idx !== groupIndex));
  };

  const addGroup = () => {
    setImageGroups((prev) => [...prev, { title: `Group ${prev.length + 1}`, images: [] }]);
  };

  const updateGroupTitle = (groupIndex: number, title: string) => {
    setImageGroups((prev) => prev.map((group, idx) => (idx === groupIndex ? { ...group, title } : group)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await upsert.mutateAsync({
      id,
      title,
      description: description || undefined,
      date: date || undefined,
      featured,
      image_url: imageUrl || undefined,
      additional_images: additionalImages,
      image_groups: imageGroups,
    });
    navigate('/declarations');
  };

  return (
    <Page>
      <h1 style={{ marginBottom: '1rem' }}>{editing ? 'Edit' : 'New'} Declaration</h1>
      <FormCard>
        <Form onSubmit={handleSubmit}>
        <label>
          Title
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label>
          Description
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label>
          Date
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          Featured
          <Select value={featured ? 'yes' : 'no'} onChange={(e) => setFeatured(e.target.value === 'yes')}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </label>
        <label>
          Thumbnail Preview
          {imageUrl && (
            <img
              src={imageUrl}
              alt={title ? `${title} thumbnail` : 'Declaration thumbnail'}
              style={{ width: '100%', objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
            />
          )}
        </label>
        <label>
          Upload Image
          <Input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  await handleUpload(e.target.files[0]);
                } catch (err) {
                  // eslint-disable-next-line no-alert
                  alert('Upload failed');
                }
              }
            }}
          />
        </label>
        <label>
          Additional Images
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {additionalImages.map((url, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <img
                  src={url}
                  alt={`Additional ${idx + 1}`}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                />
                <button
                  type="button"
                  onClick={() => setAdditionalImages(additionalImages.filter((_, i) => i !== idx))}
                  style={{ position: 'absolute', top: -6, right: -6, background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </label>
        <label>
          Add Image
          <Input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  await handleAdditionalUpload(e.target.files[0]);
                } catch (err) {
                  alert('Upload failed');
                }
              }
            }}
          />
        </label>
        <label>
          Image Groups
          <p style={{ color: 'var(--onSurfaceVariant)', margin: '4px 0' }}>Optional: organize images into named sets.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {imageGroups.map((group, groupIdx) => (
              <div
                key={groupIdx}
                style={{
                  border: '1px solid var(--outline)',
                  borderRadius: 6,
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input
                    value={group.title ?? ''}
                    placeholder="Group title"
                    onChange={(e) => updateGroupTitle(groupIdx, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeGroup(groupIdx)}
                    style={{
                      background: '#e53e3e',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(group.images ?? []).map((url, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`${group.title || 'Group'} ${idx + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeGroupImage(groupIdx, idx)}
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          background: '#e53e3e',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    if (e.target.files?.[0]) {
                      try {
                        await handleGroupImageUpload(groupIdx, e.target.files[0]);
                      } catch (err) {
                        alert('Upload failed');
                      }
                    }
                  }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <Button type="button" onClick={addGroup}>
              + Add Image Group
            </Button>
          </div>
        </label>
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save'}
        </Button>
        </Form>
      </FormCard>
    </Page>
  );
};

export default DeclarationFormPage;
